export interface AuditDiffRow {
  key?: string;
  region: string;
  section: string;
  syncoreName: string;
  metric: string;
  expected?: number;
  actual?: number | null;
  delta?: number | null;
}

export interface AuditDiffPayload {
  mismatched: AuditDiffRow[];
  missingInWarehouse: AuditDiffRow[];
  extraInWarehouse: AuditDiffRow[];
}

export interface AuditReconcileResultShape {
  expectedRows: number;
  matched: number;
  mismatched: AuditDiffRow[];
  missingInWarehouse: AuditDiffRow[];
  extraInWarehouse: AuditDiffRow[];
}

export interface AuditCursorPromptInput {
  auditRunId: number;
  system: string;
  activityDate: string;
  checkType: string;
  triggerType: string;
  status: "fail" | "error";
  startedAt?: string | null;
  finishedAt?: string | null;
  expectedRows: number;
  matched: number;
  mismatchedCount: number;
  missingCount: number;
  extraCount: number;
  diffs: AuditDiffPayload;
  selfCorrectAttempted: boolean;
  selfCorrectSyncRunId?: number | null;
  errorDetail?: string | null;
}

const DIFF_LIMIT = 50;

function formatDiffLine(d: AuditDiffRow, kind: "mismatched" | "missing" | "extra"): string {
  const name = `\`${d.syncoreName}\``;
  if (kind === "mismatched" && "expected" in d && "actual" in d && "delta" in d) {
    return `- ${d.region} ${d.section} ${name} ${d.metric}: expected ${d.expected}, warehouse ${d.actual ?? "null"} (delta ${d.delta ?? "—"})`;
  }
  if (kind === "missing" && "expected" in d) {
    return `- ${d.region} ${d.section} ${name} ${d.metric}: expected ${d.expected}, not in warehouse`;
  }
  if (kind === "extra" && "actual" in d) {
    return `- ${d.region} ${d.section} ${name} ${d.metric}: warehouse ${d.actual}, not in fresh scrape`;
  }
  return `- ${d.region} ${d.section} ${name} ${d.metric}`;
}

function appendDiffSection(
  lines: string[],
  title: string,
  rows: AuditDiffRow[],
  kind: "mismatched" | "missing" | "extra",
  auditRunId: number,
): void {
  if (rows.length === 0) return;
  lines.push(`## ${title} (${rows.length})`);
  for (const d of rows.slice(0, DIFF_LIMIT)) {
    lines.push(formatDiffLine(d, kind));
  }
  if (rows.length > DIFF_LIMIT) {
    lines.push(
      `- ... and ${rows.length - DIFF_LIMIT} more (see /audit run #${auditRunId} or warehouse.audit_runs.diffs JSON)`,
    );
  }
  lines.push("");
}

function systemLabel(system: string): string {
  const s = system.toLowerCase();
  if (s === "ca") return "CA (Canada Syncore)";
  if (s === "us") return "US (United States Syncore)";
  return system.toUpperCase();
}

export function buildAuditCursorPrompt(input: AuditCursorPromptInput): string {
  const system = input.system.toLowerCase();
  const isError = input.status === "error";
  const lines: string[] = [
    "Fix warehouse data accuracy audit failure in bb-data-warehouse.",
    "",
    "## Your mission",
    isError
      ? "The automated weekday audit crashed before it could finish comparing fresh Syncore/HubSpot data to the warehouse. Diagnose the error, fix the scraper/ingest/infrastructure issue, then re-run the audit until it passes."
      : "The automated weekday audit scraped fresh Syncore/HubSpot data, compared it to the warehouse, and numbers still do not match after one self-correct re-sync. Find the root cause (scraper, ingest, rep/team mapping, or reconcile logic), fix it, and verify the audit passes.",
    "",
    "## Start here (fresh Cursor session)",
    "1. Run `/session-start` — read CLAUDE.md, PROJECT_PLAN.md, and recent session memory before touching code.",
    "2. Read this entire prompt. Do not guess at mismatches; reproduce them locally first.",
    "3. Confirm `.env` has Syncore login creds, `HUBSPOT_ACCESS_TOKEN` (for call supplement + SDR), and `SUPABASE_DB_URL` or `SUPABASE_DB_PASSWORD` for direct Postgres.",
    "4. Reproduce the failure with the commands in **Reproduce locally** below.",
    "5. Trace each mismatch: source report UI → probe JSON (`.probe-output/`) → ingest (`bundle-daily`) → `warehouse.daily_activities` row.",
    "6. Apply the smallest correct fix. Run `npm run check` before declaring done.",
    "7. Re-run `npm run run-audit` for this system/date until status is `pass` or `corrected`.",
    "8. Run `/session-end` — update memory, PROJECT_PLAN, and commit (do not push unless asked).",
    "",
    "## What the audit checked",
    `- **Check type:** ${input.checkType}`,
    `- **System:** ${systemLabel(system)}`,
    `- **Activity date:** ${input.activityDate} (Toronto calendar day; weekday schedule audits yesterday)`,
    `- **Trigger:** ${input.triggerType}`,
    `- **Fresh sources scraped:** Syncore Calls report, Syncore Productivity (meetings), HubSpot sales-calls supplement (if token set), HubSpot SDR puller (if token set)`,
    `- **Compared to:** \`warehouse.daily_activities\` joined to \`warehouse.reps\` / \`warehouse.teams\`, keyed as \`region|section|syncoreName|metric\` (calls, meetings, sdr only — not period sales/opps)`,
    `- **Self-correct re-sync:** ${input.selfCorrectAttempted ? "already attempted once" : "not attempted (manual run may have used --no-self-correct)"}`,
  ];

  if (input.selfCorrectSyncRunId) {
    lines.push(`- **Self-correct sync_run_id:** ${input.selfCorrectSyncRunId}`);
  }

  lines.push(
    "",
    "## Audit run record",
    `- **Run ID:** #${input.auditRunId} (web UI: \`/audit\`; table: \`warehouse.audit_runs\`)`,
    `- **Status:** ${input.status}`,
  );

  if (input.startedAt) {
    lines.push(`- **Started:** ${input.startedAt}`);
  }
  if (input.finishedAt) {
    lines.push(`- **Finished:** ${input.finishedAt}`);
  }

  lines.push(
    "",
    "## Summary",
    `- Expected rows: ${input.expectedRows}`,
    `- Matched: ${input.matched}`,
    `- Mismatched: ${input.mismatchedCount}`,
    `- Missing in warehouse: ${input.missingCount}`,
    `- Extra in warehouse: ${input.extraCount}`,
    "",
  );

  if (input.errorDetail) {
    lines.push("## Error detail", "```", input.errorDetail, "```", "");
  }

  appendDiffSection(
    lines,
    "Mismatched (fresh scrape vs warehouse)",
    input.diffs.mismatched,
    "mismatched",
    input.auditRunId,
  );
  appendDiffSection(
    lines,
    "Missing in warehouse",
    input.diffs.missingInWarehouse,
    "missing",
    input.auditRunId,
  );
  appendDiffSection(
    lines,
    "Extra in warehouse",
    input.diffs.extraInWarehouse,
    "extra",
    input.auditRunId,
  );

  lines.push(
    "## Investigation playbook",
    "",
    "### Calls (total / connected)",
    "- Open Syncore Calls report for the activity date and spot-check one mismatched rep.",
    "- Scraper: `src/scrapers/calls/report.ts` — date filter, row parsing, team-prefixed rows skipped in audit expected set.",
    "- HubSpot connected supplement: `src/pullers/hubspot-sales-calls/` + merge in `src/lib/combine-calls.ts`.",
    "- Ingest: `src/ingestion/bundle-daily.ts`. Unmapped reps → partial sync; check sync_run logs.",
    "",
    "### Meetings (meetings_held)",
    "- Syncore Productivity report for the activity date.",
    "- Scraper: `src/scrapers/productivity/report.ts`.",
    "",
    "### HubSpot SDR (calls / meetings / sequences)",
    "- Puller: `src/pullers/hubspot-sdr/`. Region defaults to US; active map is `sarah` and `megan` (`erica` and `stephen` are terminated and stay in `sdr-daily-activity-refs` for audit skip).",
    "",
    "### Mapping / dimension issues",
    "- Rep names must match `warehouse.reps.syncore_ref` exactly (case/spacing).",
    "- Display map: `src/data/syncore-team-display-map.json`. HubSpot rep map: `src/data/hubspot-sales-rep-map.json`.",
    "- Run `npm run verify-ingest` for dimension health.",
    "- Docs: `docs/SCHEMA_MAPPING.md`, `docs/SPOT_CHECK_GUIDE.md`.",
    "",
    "## Likely files",
    "- `src/lib/audit-daily.ts` — audit orchestration and expected-row builder",
    "- `src/lib/reconcile.ts` — warehouse load + comparison keys",
    "- `src/scrapers/calls/report.ts`",
    "- `src/scrapers/productivity/report.ts`",
    "- `src/pullers/hubspot-sales-calls/`",
    "- `src/pullers/hubspot-sdr/`",
    "- `src/ingestion/bundle-daily.ts`",
    "- `src/lib/combine-calls.ts`",
    "- `src/sync-daily.ts` — what self-correct re-sync runs",
    "",
    "## Reproduce locally",
    "```bash",
    `# Re-run the full audit (scrapes Syncore fresh, compares warehouse, self-corrects once)`,
    `npm run run-audit -- --system=${system} --date=${input.activityDate}`,
    "",
    `# Compare warehouse to spot-check CSV for the same date`,
    `npm run reconcile -- --date=${input.activityDate}`,
    "",
    `# Generate handoff CSV/MD for manual Syncore UI check`,
    `npm run spot-check -- --system=${system} --date=${input.activityDate}`,
    "",
    `# Watch individual scrapers (optional)`,
    `HEADLESS=false npm run scrape -- calls --system=${system} --from=${input.activityDate} --to=${input.activityDate}`,
    `HEADLESS=false npm run scrape -- productivity --system=${system} --from=${input.activityDate} --to=${input.activityDate}`,
    "```",
    "",
    "## Inspect warehouse (SQL)",
    "```sql",
    `-- Audit run row`,
    `select id, status, system, activity_date, expected_rows, matched, mismatched_count, missing_count, extra_count, diffs`,
    `from warehouse.audit_runs where id = ${input.auditRunId};`,
  );

  if (input.selfCorrectSyncRunId) {
    lines.push(
      "",
      `-- Self-correct sync run`,
      `select id, status, trigger_type, started_at, finished_at, error_detail from warehouse.sync_runs where id = ${input.selfCorrectSyncRunId};`,
    );
  }

  lines.push(
    "",
    `-- Daily activity facts for ${input.activityDate}`,
    `select t.region, r.syncore_ref, da.total_calls, da.connected_calls, da.meetings_held, da.sequences`,
    `from warehouse.daily_activities da`,
    `join warehouse.reps r on r.id = da.rep_id`,
    `join warehouse.teams t on t.id = r.team_id`,
    `where da.activity_date = '${input.activityDate}'::date`,
    `order by t.region, r.syncore_ref;`,
    "```",
    "",
    "## Done when",
    "- `npm run run-audit -- --system=" +
      system +
      " --date=" +
      input.activityDate +
      "` exits 0 with status `pass` or `corrected`.",
    "- `npm run check` passes.",
    "- Root cause is understood (not just a one-off re-sync). If mapping gap, note whether seed/migration is needed.",
  );

  return lines.join("\n");
}

export function buildAuditCursorPromptFromReconcile(params: {
  auditRunId: number;
  system: string;
  dateIso: string;
  checkType?: string;
  triggerType?: string;
  result: AuditReconcileResultShape;
  selfCorrectAttempted: boolean;
  selfCorrectSyncRunId?: number | null;
}): string {
  return buildAuditCursorPrompt({
    auditRunId: params.auditRunId,
    system: params.system,
    activityDate: params.dateIso,
    checkType: params.checkType ?? "daily_activity",
    triggerType: params.triggerType ?? "scheduled",
    status: "fail",
    expectedRows: params.result.expectedRows,
    matched: params.result.matched,
    mismatchedCount: params.result.mismatched.length,
    missingCount: params.result.missingInWarehouse.length,
    extraCount: params.result.extraInWarehouse.length,
    diffs: {
      mismatched: params.result.mismatched,
      missingInWarehouse: params.result.missingInWarehouse,
      extraInWarehouse: params.result.extraInWarehouse,
    },
    selfCorrectAttempted: params.selfCorrectAttempted,
    selfCorrectSyncRunId: params.selfCorrectSyncRunId,
  });
}

export function buildAuditErrorCursorPrompt(params: {
  auditRunId: number;
  system: string;
  dateIso: string;
  checkType?: string;
  triggerType?: string;
  errorDetail: string;
}): string {
  return buildAuditCursorPrompt({
    auditRunId: params.auditRunId,
    system: params.system,
    activityDate: params.dateIso,
    checkType: params.checkType ?? "daily_activity",
    triggerType: params.triggerType ?? "scheduled",
    status: "error",
    expectedRows: 0,
    matched: 0,
    mismatchedCount: 0,
    missingCount: 0,
    extraCount: 0,
    diffs: { mismatched: [], missingInWarehouse: [], extraInWarehouse: [] },
    selfCorrectAttempted: false,
    errorDetail: params.errorDetail,
  });
}
